<template>
  <div class="d-flex footage ma-0 pa-0">
    <div v-show="camera" class="d-flex flex-column width100">
      <div class="d-flex flex-grow-1 flex-shrink-1 pa-0">
        <div class="flex-grow-1 flex-shrink-1 d-flex">
          <v-row no-gutters class="px-3">
            <v-col>
              <div v-if="camera" class="footage-player d-flex justify-center ">
                <v-toolbar flat height="36" class="camera-toolbar top">
                  <v-toolbar-title class="camera-name px-1 mr-1">
                    {{ camera.name }}
                  </v-toolbar-title>
                  <v-icon
                    v-if="camera.status && camera.status.recordingOnCloud"
                    size="12"
                    color="red"
                  >
                    fas fa-circle
                  </v-icon>
                  <v-spacer></v-spacer>
                  <div class="footage-player-stamp">
                    {{ offsetStamp }} <tr/>
                    <span v-if="qrCodeInfo && !isNaN(qrCodeInfo.speed)">
                      {{ travelSpeed }}
                    </span>
                  </div>
                </v-toolbar>
                <span
                  v-if="!currentRecording && showTimeline"
                  class="no-recording"
                >
                  <div class="icon">
                    <v-icon color="white" size="50px">fas fa-video-slash</v-icon>
                  </div>
                  <p class="mt-5 white--text headline">
                    {{ $t("No footage available for the selected time") }}
                  </p>
                </span>
                <transition name="fade">
                  <template>
                    <button class="play-pause" v-show="!isPlaying && !isDragging && !!currentRecording && !isBufferingVideo" @click="togglePlay"> 
                      <svg
                        id="play-svg-1"
                        ref="playSvg"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 32 32"
                      >
                        <path ref="path" d="M11,8 L26,16 11,24 11,8">
                          <animate
                            ref="animate"
                            attributeName="d"
                            fill="freeze"
                            dur="0.1s"
                            calc-mode="spline"
                            keySplines="0.19 1 0.22 1"
                          />
                        </path>
                      </svg>
                    </button>
                  </template>
                </transition>
                <div v-if="supportsDewarping === false && codec !== 'mjpeg'" style="width: 100%" @contextmenu.prevent="$refs.menu.open">
                  <video
                    ref="videoPlayer"
                    class="video-js"
                    playsinline
                    :class="{
                      offline: camera && camera.status && !camera.status.online
                    }"
                  ></video>
                </div>
                <div 
                  ref="dewarp-fullscreen" 
                  class="footage-player" 
                  v-if="supportsDewarping || codec === 'mjpeg'"
                >
                  <div 
                    v-if="supportsDewarping" 
                    ref="dewarpingContainer" 
                    class="immervisionContainer" 
                    @contextmenu.prevent="$refs.menu.open"
                  >
                    <video-movement-controls v-if="immervisionPlayer" class="dewarp-arrow-controls" @arrowPress="handleRotationInput"></video-movement-controls>
                  </div>
                  <audio v-if="supportsDewarping" v-show="false" ref="dewarpAudioPlayer"></audio>
                  <img
                    ref="mjpegPlayer"
                    v-if="codec === 'mjpeg' && isPlaying"
                    class="mjpeg-player"
                    :src="mp4Url"
                    @load="imgLoaded"
                  />
                  <img
                    ref="mjpegPlayer"
                    v-else-if="codec === 'mjpeg'"
                    class="mjpeg-player"
                    :src="currentSnapshot"
                  />
                  <v-toolbar
                    flat
                    height="30"
                    class="camera-toolbar bottom"
                    id="dewarp-player-toolbar"
                  >
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn small icon @click="togglePlay" v-bind="attrs" v-on="on">
                          <svg
                            id="play-svg-2"
                            ref="playSvg2"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 32 32"
                          >
                            <path ref="path2" d="M11,8 L26,16 11,24 11,8">
                              <animate
                                ref="animate2"
                                attributeName="d"
                                fill="freeze"
                                dur="0.1s"
                                calc-mode="spline"
                                keySplines="0.19 1 0.22 1"
                              />
                            </path>
                          </svg>
                        </v-btn>
                      </template>
                      <span>{{ isPlaying ? $t("Pause") : $t("Play") }}</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">
                          <v-menu top dark attach="#dewarp-player-toolbar" nudge-top="28">
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn v-bind="attrs" v-on="on" text small dense color="white">
                                {{ immervisionPlayer ? immervisionPlayer.video_html.playbackRate+"x" : "1x" }}
                              </v-btn>
                            </template>
                            <v-list dense >
                              <v-list-item
                                v-for="(item, index) in playerOptions.playbackRates"
                                :key="index"
                                @click="setDewarpPlayBackRate(item)"
                                :class="{'selected-playbackrate': item === immervisionPlayer && immervisionPlayer.video_html.playbackRate }"
                                dense
                              >
                                <v-list-item-title>{{ item }}x</v-list-item-title>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                        </span>
                      </template>
                      <span>{{ $t("Playback Rate") }}</span>
                    </v-tooltip>

                    <template v-if="playerHasAudioTrack">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn v-bind="attrs" v-on="on" text small dense @click="setDewarpPlayerVolume(true)">
                            <v-icon color="secondary" size="15">
                              {{ player.muted() ? 'fa-volume-mute' : dewarpVolumeIsLow ? 'fa-volume-down' : 'fa-volume-up'}} 
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>{{ player.muted() ? $t("Unmute") : $t("Mute") }}</span>
                      </v-tooltip>
                      <div class="dewarp-volume-slider" v-show="!player.muted()">
                        <v-slider
                          track-color="secondary" thumb-color="secondary"
                          hide-details dense
                          v-model="dewarpVolume"
                          min="0" max="100"
                          @change="setDewarpPlayerVolume(false)"
                        ></v-slider>
                      </div>
                    </template>
               
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn 
                          icon 
                          @click="takeSnapshot"
                          v-bind="attrs"
                          v-on="on"
                          >
                          <v-icon color="secondary" size="15">fa-camera</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("Take Snapshot") }}</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn 
                          icon
                          @click="backwardsTimeJump"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-avatar size="21">
                            <img src="../assets/images/replay-10.png">
                          </v-avatar>
                        </v-btn>
                      </template>
                      <span>{{ $t("Go back 10 seconds") }}</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn 
                          icon
                          @click="forwardTimeJump"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-avatar size="21">
                            <img src="../assets/images/forward-10.png">
                          </v-avatar>
                        </v-btn>
                      </template>
                      <span>{{ $t("Advance 10 seconds") }}</span>
                    </v-tooltip>
                    <v-spacer></v-spacer>
                    <button class="go-live-dewarp" @click="goLive()">{{ $t("Live") }}</button>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn 
                          icon 
                          @click="toggleDewarpFullscreen"
                          v-bind="attrs"
                          v-on="on"
                          >
                          <v-icon v-show="dewarpFullscreen" color="secondary" size="15">fa-compress</v-icon>
                          <v-icon v-show="!dewarpFullscreen" color="secondary" size="15">fa-expand</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("Full Screen") }}</span>
                    </v-tooltip>
                  </v-toolbar>
                </div>
              </div>
            </v-col>
            <v-col v-if="supportsDewarping && showMap && gpsDetected" cols="5" class="ml-1">
              <Maps :position="cameraCoordinates"></Maps>
            </v-col>
          </v-row>
        </div>
      </div>
      <div ref="test">
        <v-container fluid class="py-0">
          <v-row justify="space-between" class="timeline-action-bar px-8">
            <v-col class="pa-0">
              <v-row align="center" justify="center">
                <v-col cols="auto" class="pa-0">
                  <div class="button-group">
                    <v-tooltip
                      nudge-bottom="20"
                      :disabled="!shouldCollapseButtons"
                      bottom
                    >
                      <template v-slot:activator="{ on }">
                        <span v-on="on">
                          <v-btn
                            small
                            icon
                            class="mx-1 ma-0 float-left"
                            @click.exact="gotoPreviousEvent"
                          >
                            <v-icon color="grey darken-2" size="15">
                              fa-step-backward
                            </v-icon>
                          </v-btn>
                        </span>
                      </template>
                      <span>{{ $t("Jump to previous event") }}</span>
                    </v-tooltip>
                    <p
                      v-if="!shouldCollapseButtons"
                      class="text-uppercase ma-0 mt-1 float-left"
                    >
                      {{ $t("Events") }}
                    </p>
                    <v-tooltip
                      nudge-bottom="20"
                      :disabled="!shouldCollapseButtons"
                      bottom
                    >
                      <template v-slot:activator="{ on }">
                        <span v-on="on">
                          <v-btn
                            small
                            icon
                            class=" mx-1 ma-0 float-left"
                            @click.exact="gotoNextEvent"
                          >
                            <v-icon color="grey darken-2" size="15">
                              fa-step-forward
                            </v-icon>
                          </v-btn>
                        </span>
                      </template>
                      <span>{{ $t("Jump to next event") }}</span>
                    </v-tooltip>
                  </div>
                </v-col>
                <v-col cols="auto" class="pa-0">
                  <div class="button-group" v-if="codec !== 'mjpeg'">
                    <v-tooltip
                      nudge-bottom="20"
                      :disabled="!shouldCollapseButtons"
                      bottom
                    >
                      <template v-slot:activator="{ on }">
                        <span v-on="on">
                          <v-btn
                            small
                            icon
                            class="mx-1 ma-0 float-left"
                            :disabled="!mp4Url"
                            @click.exact="showPreviousFrame"
                          >
                            <v-icon color="grey darken-2" size="23">
                              fa-caret-left
                            </v-icon>
                          </v-btn>
                        </span>
                      </template>
                      <span>{{ $t("Previous frame") }}</span>
                    </v-tooltip>
                    <p
                      v-if="!shouldCollapseButtons"
                      class="text-uppercase ma-0 mt-1 float-left"
                    >
                      {{ $t("Frame") }}
                    </p>
                    <v-tooltip
                      nudge-bottom="20"
                      :disabled="!shouldCollapseButtons"
                      bottom
                    >
                      <template v-slot:activator="{ on }">
                        <span v-on="on">
                          <v-btn
                            small
                            icon
                            class="mx-1 ma-0 float-left"
                            :disabled="!mp4Url"
                            @click="showNextFrame"
                          >
                            <v-icon color="grey darken-2" size="23">
                              fa-caret-right
                            </v-icon>
                          </v-btn>
                        </span>
                      </template>
                      <span>{{ $t("Next frame") }}</span>
                    </v-tooltip>
                  </div>
                </v-col>
                <v-col cols="auto" class="pa-0">
                  <div class="button-group">
                    <v-tooltip nudge-bottom="20" bottom>
                      <template v-slot:activator="{ on }">
                        <span v-on="on">
                          <v-btn
                            small
                            icon
                            class="my-0 mx-1 float-left"
                            :disabled="!canZoomOut"
                            @click="zoomOut"
                          >
                            <v-icon color="grey darken-2" size="18">
                              fa-search-minus
                            </v-icon>
                          </v-btn>
                        </span>
                      </template>
                      <span>{{ $t("Zoom Out") }}</span>
                    </v-tooltip>
                    <v-tooltip nudge-bottom="20" bottom>
                      <template v-slot:activator="{ on }">
                        <span v-on="on">
                          <v-btn
                            small
                            icon
                            class="my-0 mx-1 float-left"
                            :disabled="!canZoomIn"
                            @click="zoomIn"
                          >
                            <v-icon color="grey darken-2" size="18">
                              fa-search-plus
                            </v-icon>
                          </v-btn>
                        </span>
                      </template>
                      <span>{{ $t("Zoom In") }}</span>
                    </v-tooltip>
                  </div>
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="auto" class="py-0">
                  <v-menu
                    v-model="dateDialog"
                    :close-on-content-click="false"
                    :nudge-top="5"
                    transition="scale-transition"
                    top
                    left
                    min-width="290px"
                    class="mr-0"
                  >
                    <template v-slot:activator="{ on: { click } }">
                      <v-row
                        @click="click"
                        class="date-time-select float-right mr-1 px-3"
                      >
                        <div class="mt-1">
                          {{ displayDate }}
                        </div>
                        <v-icon
                          size="17px"
                          color="grey darken-2"
                          class="ml-2 pt-0"
                          >fa-calendar-day</v-icon
                        >
                      </v-row>
                    </template>
                    <v-date-picker
                      v-if="dateDialog"
                      v-model="date"
                      :locale="locale"
                      color="primary"
                      :min="minimumDate"
                      :max="today"
                      @input="dateSelected"
                      no-title
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </v-col>
            <v-col class="pa-0">
              <v-row justify="start">
                <v-col cols="auto" class="py-0">
                  <v-row class="date-time-select time ml-0 pa-0">
                    <vue-timepicker
                      v-click-outside="closeTimePicker"
                      ref="timepicker"
                      auto-scroll
                      hide-clear-button
                      input-width="100%"
                      fixed-dropdown-button
                      format="HH:mm:ss"
                      @change="timeSelected"
                      v-model="displayTime"
                      @open="dropdownStatus = true"
                      @close="dropdownStatus = false"
                    >
                      <template v-slot:dropdownButton>
                        <v-icon
                          size="17px"
                          color="grey darken-2"
                          class="ml-2 pt-0"
                          >fa-clock</v-icon>
                      </template>
                    </vue-timepicker>
                  </v-row>
                </v-col>
                <v-spacer></v-spacer>
                <v-col v-if="supportsDewarping && browser !== 'Safari' " cols="auto" class="pa-0">
                  <v-tooltip :disabled="!(!currentRecording  || shouldCollapseButtons)" bottom>
                    <template v-slot:activator="{ on }">
                      <span v-on="on">
                        <v-btn
                          class="px-4"
                          :disabled="(!currentRecording || (!cameraCoordinates.latitude || !cameraCoordinates.latitude )) && !showMap"
                          color="#eeeeee"
                          elevation="0"
                          height="32"
                          @click.exact="toggleMapView"
                        >
                          <p v-if="!shouldCollapseButtons" class="mb-0">{{ $t("Map") }}</p>
                          <v-icon
                            size="16"
                            class="mb-1"
                            :class="{ 'ml-2': !shouldCollapseButtons }"
                            color="grey darken-2"
                          >
                            fas fa-map-marked-alt
                          </v-icon>
                        </v-btn>
                      </span>
                    </template>
                    <span>{{ mapTooltipText }}</span>
                  </v-tooltip>
                </v-col>
                <v-col cols="auto" class="pa-0">
                  <DeleteRecordings
                    v-if="codec !== 'mjpeg' && !hideDeleteRecordings"
                    class="ml-1"
                    :camera="camera"
                    @pause="pauseVideo"
                    @updateRecordings="updateRecordings"
                    :currentTime="currentTime"
                    :currentChunk="selectedRecordingChunk"
                    :currentRecording="currentRecording"
                    :timeLevel="timeLevel"
                    :eventFilter="selectedEventFilters"
                    :chunks="recordingChunks"
                    :mini="shouldCollapseButtons"
                    :offset="offset"
                    :rights="rights"
                  ></DeleteRecordings>
                </v-col>
                <v-col cols="auto" class="pa-0">
                  <ShareVideoDialog
                    v-if="!hideShareVideoDialog"
                    class="ml-1"
                    :camera="camera"
                    @pause="pauseVideo"
                    :currentTime="currentTime"
                    :mini="shouldCollapseButtons"
                    :currentRecording="currentRecording"
                    :timeLevel="timeLevel"
                    :eventFilter="selectedEventFilters"
                  ></ShareVideoDialog>
                </v-col>
                <v-col 
                  cols="auto" 
                  class="pa-0"
                >
                  <FootageExportDialog
                    v-if="!hideFootageExportDialog"
                    class="ml-1"
                    :camera="camera"
                    @pause="pauseVideo"
                    :currentTime="currentTime"
                    :currentRecording="currentRecording"
                    :mini="shouldCollapseButtons"
                    :offset="offset"
                    :accountTimeOffset="accountTimeZoneOffset"
                    :rights="rights"
                    :resolution="resolution"
                    :openSnapshot="openSnapshot"
                    :showOnlySnap="false"
                  ></FootageExportDialog>
                </v-col>
                <v-col cols="auto" class="pa-0">
                  <v-tooltip :disabled="!shouldCollapseButtons" bottom>
                    <template v-slot:activator="{ on }">
                      <span v-on="on">
                        <v-badge
                          color="rgba(0,0,0,0.5)"
                          class="ml-1"
                          offset-y="14"
                          :content="eventFilterCount"
                          :value="eventFilterCount"
                          overlap
                        >
                          <v-btn
                            :disabled="!eventFilters.length"
                            small
                            color="#eeeeee"
                            elevation="0"
                            height="32"
                            @click.exact="showFilters = true"
                          >
                            <p
                              v-if="!shouldCollapseButtons"
                              class="filter-text mb-0"
                            >
                              {{ $t("Filter") }}
                            </p>
                            <v-icon
                              color="grey darken-2"
                              class="mx-1"
                              :class="{ 'ml-2': !shouldCollapseButtons }"
                              size="18"
                            >
                              fa-filter
                            </v-icon>
                          </v-btn>
                        </v-badge>
                      </span>
                    </template>
                    <span>{{ !eventFilters.length ? $t("No filters available") : $t("Filter timeline") }}</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </div>

      <time-line
        ref="timeline"
        :recordings="recordings"
        :showTimeline="showTimeline"
        :events="filteredEvents"
        :eventTypes="eventTypes"
        :chunks="recordingChunks"
        :timeLevel="timeLevel"
        :currentTime="currentTime"
        :from="fromTime"
        :till="tillTime"
        :time-span="timeSpan"
        :date="date"
        :timing="timing"
        :timelineOffset="offset"
        @drag="dragTimeline"
        @recordingSelected="recordingSelected"
        @eventSelected="eventSelected"
        @dragTime="onDraggingTimeUpdate"
        @dragStart="dragStart"
        @dragEnd="dragEnd"
      ></time-line>
    </div>
    <vue-context class="pl-0 menuContext" ref="menu">
      <li>
        <a
          class="d-inline-block menuContextItems"
          @click.prevent="openDebugTools()"
        >
          <v-icon color="white" class="pr-2">fas fa-bug</v-icon>
          {{ $t("Open Debug Tools") }}
        </a>
      </li>
      <li v-if="mp4Url">
        <a
          class="d-inline-block menuContextItems"
          :href="mp4Url"
          target="_blank"
        >
          <v-icon color="white" class="pr-2"
            >fas fa-external-link-square-alt</v-icon
          >
          {{ $t("Open Video Url in New Tab") }}
        </a>
      </li>
    </vue-context>
    <v-dialog v-model="showFilters" hide-overlay width="500">
      <v-card>
        <div class="pa-3">
          <v-chip
            v-for="filter in eventFilters"
            :key="filter"
            class="mt-2 filter-chips ml-1 mr-1"
            :color="filterTextColor(filter)"
            @click="filterClicked(filter)"
          >
            {{ getTranslatedEventType({ type: filter }) }}
          </v-chip>
        </div>
        <v-card-actions class="d-flex justify-end">
          <v-btn depressed @click.stop="showFilters = false">
            {{ $t("Close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="debugTools" hide-overlay width="600">
      <v-card>
        <v-card-text>
          <h3 class="text-center pt-2">{{ $t("Debug Tools") }}</h3>
          <v-list>
            <v-row>
              <v-col>
                {{ $t("Current Chunk StartTime") }}
              </v-col>
              <v-col>
                <span v-if="selectedRecordingChunk">
                  {{ selectedRecordingChunk.startTime }}
                </span>
                <span v-else>
                  <v-skeleton-loader
                    min-width="250"
                    type="heading"
                    tile
                  ></v-skeleton-loader>
                </span>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                {{ $t("Current Chunk EndTime") }}
              </v-col>
              <v-col>
                <span v-if="selectedRecordingChunk">
                  {{ selectedRecordingChunk.endTime }}
                </span>
                <span v-else>
                  <v-skeleton-loader
                    min-width="250"
                    type="heading"
                    tile
                  ></v-skeleton-loader>
                </span>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                {{ $t("Recordings In Current Chunk") }}
              </v-col>
              <v-col>
                <span v-if="recordingChunks && recordingChunks.length > 0">
                  {{ recordingChunks.length }}
                </span>
                <span v-else>
                  <v-skeleton-loader
                    min-width="250"
                    type="heading"
                    tile
                  ></v-skeleton-loader>
                </span>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                {{ $t("Current Recording ID") }}
              </v-col>
              <v-col>
                <span v-if="selectedRecordingChunk">
                  {{
                    currentRecording
                      ? currentRecording.recordingId
                      : "Invalid recording"
                  }}
                </span>
                <span v-else>
                  <v-skeleton-loader
                    min-width="250"
                    type="heading"
                    tile
                  ></v-skeleton-loader>
                </span>
              </v-col>
            </v-row>
          </v-list>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn depressed @click.stop="debugTools = false">
            {{ $t("Close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card
      v-show="hoveredEvent"
      id="event-tooltip"
      ref="eventTooltip"
      class="event-tooltip pa-1"
    >
      <template v-if="hoveredEvent">
        <v-card-title class="mt-1 py-1 px-2">
          {{ new Date(hoveredEvent.timeStamp).toISOString().split("T")[0] }}
          <v-spacer></v-spacer>
          {{
            new Date(hoveredEvent.timeStamp)
              .toISOString()
              .split("T")[1]
              .substr(0, 8)
          }}
        </v-card-title>
        <v-card-text class="pa-1">
          <v-container class="pa-1">
            <v-row>
              <v-col cols="12" class="event-image-hover">
                <event-image :event="hoveredEvent"></event-image>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-title class="mb-1 py-1 px-2">
          {{ $t(hoveredEvent.type) }}
        </v-card-title>
      </template>
    </v-card>
  </div>
</template>

<script>
import TimeLine from "./base/TimeLine";
import FootageExportDialog from "./base/FootageExportDialog";
import ShareVideoDialog from "./base/ShareVideoDialog";
import DeleteRecordings from "./base/DeleteRecordings";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import StompClient from "../service/StompClient";
import { store } from "@/store";
import restapi from "../service/CMApi";
import calculateAspectRatio from "calculate-aspect-ratio";
import * as Bowser from "bowser";
import VueContext from "vue-context";
import { saveFile, pad } from "../service/main";
import "vue-context/src/sass/vue-context.scss";
import VueTimepicker from "vue2-timepicker";
import "vue2-timepicker/dist/VueTimepicker.css";
import Sentry from '../plugins/sentry';
import _ from "lodash";
import { getOffsetStamp } from '../service/time';
import VideoMovementControls from './base/VideoMovementControls';
import Maps from './base/Maps';
import parseQrCode from "../service/qrCodeParser";
import { api as fullscreen } from 'vue-fullscreen';

export default {
  name: "Footage",
  components: {
    TimeLine,
    VueContext,
    FootageExportDialog,
    ShareVideoDialog,
    DeleteRecordings,
    VueTimepicker,
    VideoMovementControls,
    Maps,
  },
  props: [
    "camera",
    "audioAvailable",
    "isSDK",
    "hideFootageExportDialog",
    "hideShareVideoDialog",
    "hideDeleteRecordings",
    "hideGoLive",
    "accountTimeZoneOffset",
    "rights"
  ],
  data() {
    return {
      newTime: null,
      timeAsHMS: null,
      getEventInterval: null,
      stompClient: null,
      snapshot: null,
      previousSnapshot: null,
      recordings: [],
      firstRecording: null,
      selectedRecording: null,
      selectedEventFilters: [],
      events: [],
      firstEvent: null,
      selectedEvent: null,
      currentTime: null,
      date: new Date().toISOString().substr(0, 10),
      displayDate: new Date().toISOString().substr(0, 10),
      dateDialog: false,
      timeDialog: false,
      timeSpan: null,
      timeSpans: [12, 24, 48, 96, 192],
      allEventsLoaded: false,
      eventsRetrievalPending: false,
      hoveredEvent: null,
      isDragging: false,
      nextEvent: null,
      playerOptions: {
        bigPlayButton: false,
        autoplay: false,
        preload: "auto",
        loop: false,
        muted: true,
        responsive: true,
        inactivityTimeout: 0,
        playbackRates: [0.5, 1.0, 2.0, 5.0, 10.0],
        errorDisplay: false,
        controls: true,
        controlBar: {
          volumePanel: this.audioAvailable,
          durationDisplay: false,
          playToggle: true,
          currentTimeDisplay: false,
          progressControl: false,
          remainingTimeDisplay: false,
          pictureInPictureToggle: false
        }
      },
      playbackRate: 1,
      playerHeight: null,
      previousEvent: null,
      secondPreviousEvent: null,
      secondPreviousEventTimer: null,
      recordingChunks: [],
      recordingsRetrievalPending: false,
      requestId: null,
      selectedRecordingChunk: null,
      selectedStartTime: null,
      showFilters: false,
      debugTools: false,
      useRecordingChunks: true,
      forcePausePlayback: false,
      timeLevel: 3,
      timing: 0,
      minute: 60000,
      hour: 3600000,
      day: 86400000,
      showTimeline: false,
      browser: null,
      displayTime: "00:00:00",
      counter: 0,
      maxEventDate: new Date().getTime(),
      minEventDate: new Date().getTime(),
      isPlaying: false,
      isLoading: null,
      isBufferingVideo: false,
      playButtonTimeout: null,
      mouseMoved: false,
      mouseMovedTimeout: null,
      firstPlay: true,
      codec: null,
      currentSnapshot: null,
      mjpegTimeInterval: null,
      dropdownStatus: false,
      shouldCollapseButtons: false,
      footageShouldDestroy: false,
      player: null,
      offsetStamp: "",
      immervisionPlayer: null,
      immervisionControl: null,
      availableEventTypes: [],
      qrCodeInfo: null,
      dewarpFullscreen: false,
      dewarpVolume: 100,
      showMap: false,
      gpsDetected: false,
      updateRecordingInterval: null,
      autoPlayChunkUpdate: false,
      timelineInteracted: false,
      openSnapshot: false
    };
  },
  computed: {
    cameraCoordinates() {
      return { latitude: this.qrCodeInfo?.latitude, longitude: this.qrCodeInfo?.longitude }
    },
    eventTypes() {
      const eventTypes = {}
      store.getters.eventTypes.forEach(type => {
        eventTypes[type.eventType] = type
      });
      return eventTypes
    },
    eventTypesNames() {
      return Object.keys(this.eventTypes)
    },
    eventFilters() {
      const filters = []

      this.eventTypesNames.forEach(type => {
        let actualType = null

        if (type.indexOf('object') !== -1) {
          actualType = "AnalyticsObject"
        } else if (type.indexOf('person') !== -1) {
          actualType = "AnalyticsPerson"
        } else {
          actualType = type
        }

        if (!filters.includes(actualType)) {
          switch (actualType) {
            case "vmdDetected":
              if (this.supportsVideoMotion) filters.push(actualType);
              break;
            case "pirDetected":
              if (this.supportsPir) filters.push(actualType);
              break;
            case "audioDetected":
              if (this.supportsAudio) filters.push(actualType);
              break;
            case "AnalyticsObject":
              if (this.supportsAnalyticsObject) filters.push(actualType);
              break;
            case "AnalyticsPerson":
              if (this.supportsAnalyticsPerson) filters.push(actualType);
              break;
            default:
              if (this.availableEventTypes.includes(actualType)) filters.push(actualType)
              break;
          }
        }
      })

      return filters
    },
    hashEvents() {
      let hash = {};
      this.events.forEach(element => {
        hash[element.eventId] = element;
      });
      return hash;
    },
    locale() {
      return this.$locale.getCurrentLanguage();
    },
    hashRecordings() {
      let hash = {};
      this.recordings.forEach(element => {
        hash[element.recordingId] = element;
      });
      return hash;
    },
    userId() {
      return store.getters.userId;
    },
    cameraId() {
      return this.camera.cameraId;
    },
    minimumDate() {
      let dt = new Date();
      if (this.firstRecording && new Date(this.firstRecording.startTime) < dt)
        dt = new Date(this.firstRecording.startTime);
      if (this.firstEvent && new Date(this.firstEvent.timestamp) < dt)
        dt = new Date(this.firstEvent.timestamp);
      return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(
        dt.getDate()
      )}`;
    },
    today() {
      const dt = new Date();
      return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(
        dt.getDate()
      )}`;
    },
    minDateForSelectedCamera() {
      if (this.camera && this.camera.subscriptionRights) {
        const dt = new Date();
        dt.setDate(
          dt.getDate() - this.camera.subscriptionRights.daysOfRecordings
        );
        return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(
          dt.getDate()
        )}`;
      }
      return null;
    },
    time: {
      get() {
        const dt = new Date(this.currentTime - this.offset);
        this.newTime = { hours: dt.getHours(), minutes: dt.getMinutes() };
        let time = `${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(
          dt.getSeconds()
        )}`;
        this.displayTime = time;
        return time;
      },
      set(value) {
        this.newTime.hours = parseInt(value.split(":")[0]);
        this.newTime.minutes = parseInt(value.split(":")[1]);
      }
    },
    centerTime() {
      const coeff = 1000 * 60;
      const dt = new Date(this.currentTime);
      return new Date(Math.round(dt.getTime() / coeff) * coeff);
    },
    mp4Url() {
      let mp4 = this.selectedRecordingChunk
        ? this.selectedRecordingChunk.hasOwnProperty("blobUrl")
          ? this.selectedRecordingChunk.blobUrl
          : this.selectedRecordingChunk.urls.mp4Https
        : null;
      if (
        this.codec === "mjpeg" &&
        this.selectedRecording &&
        this.selectedRecording.urls.multipartaudioHttps
      ) {
        if (this.selectedStartTime === null)
          this.selectedStartTime = this.currentTime;
        const ts = new Date(this.selectedStartTime)
          .toISOString()
          .replace("Z", "+0000");
        mp4 = `${
          this.selectedRecording.urls.multipartaudioHttps
        }?start_time=${encodeURIComponent(ts)}`;
      }
      if (mp4 === null && !this.useRecordingChunks && this.selectedRecording) {
        if (this.selectedStartTime === null)
          this.selectedStartTime = this.currentTime;
        const ts = new Date(this.selectedStartTime)
          .toISOString()
          .replace("Z", "+0000");
        mp4 = `${
          this.selectedRecording.urls.mp4Https
        }?from=${encodeURIComponent(ts)}`;
      }
      const c = mp4 && mp4.indexOf("?") === -1 ? "?" : "&";
      return mp4 ? `${mp4}${c}access_token=${this.token}` : null;
    },
    todaysEvents() {
      const dt = new Date();
      const today = dt.toDateString();
      return this.events
        ? this.events.filter(e => {
            const eDate = new Date(e.timestamp);
            return eDate.toDateString() === today;
          })
        : null;
    },
    fromTime() {
      const dt = new Date(
        this.zoomLevel *
          1000 *
          Math.round(this.currentTime / (this.zoomLevel * 1000))
      );
      dt.setMinutes(dt.getMinutes() - this.timeSpan / 2);
      return dt.getTime();
    },
    tillTime() {
      const dt = new Date(
        this.zoomLevel *
          1000 *
          Math.round(this.currentTime / (this.zoomLevel * 1000))
      );
      dt.setMinutes(dt.getMinutes() + this.timeSpan / 2);
      return dt.getTime();
    },
    token() {
      return restapi.token;
    },
    framerate() {
      return this.camera && this.camera.footageSettings
        ? this.camera.footageSettings.rec.video.framerate
        : null;
    },
    resolution() {
      return this.camera && this.camera.footageSettings
        ? this.camera.footageSettings.rec.video.resolution
        : null;
    },
    aspectRatio() {
      if (this.resolution) {
        const resolutions = this.resolution.split("x");
        const aspectRatio = calculateAspectRatio(
          resolutions[0],
          resolutions[1]
        );
        return aspectRatio;
      }
      return null;
    },
    eventFilterCount() {
      return this.selectedEventFilters.length;
    },
    filteredEvents() {
      let selectedEventFilters = this.selectedEventFilters.length > 0 ? this.selectedEventFilters : this.eventFilters;

      if (selectedEventFilters.includes("AnalyticsObject")) {
        selectedEventFilters = [
          ...selectedEventFilters,
          ...["objectDetected", "objectInAreaDetected", "objectTripwireDetected"]
        ];
      }
      if (selectedEventFilters.includes("AnalyticsPerson")) {
        selectedEventFilters = [
          ...selectedEventFilters,
          ...["personDetected", "personInAreaDetected", "personTripwireDetected"]
        ];
      }

      return this.events.filter(e => {
        return selectedEventFilters.includes(e.type);
      });
    },
    supportsAnalyticsObject() {
      return this.camera && this.camera.detectionCapabilities
        ? this.camera.detectionCapabilities.analyticsObjectArea ||
            this.camera.detectionCapabilities.analyticsObjectTripwire
        : null;
    },
    supportsAnalyticsPerson() {
      return this.camera && this.camera.detectionCapabilities
        ? this.camera.detectionCapabilities.analyticsPersonArea ||
            this.camera.detectionCapabilities.analyticsPersonTripwire
        : null;
    },
    supportsPir() {
      return this.camera && this.camera.detectionCapabilities
        ? this.camera.detectionCapabilities.pir
        : null;
    },
    supportsAudio() {
      return this.camera && this.camera.detectionCapabilities
        ? this.camera.detectionCapabilities.audio
        : null;
    },
    supportsVideoMotion() {
      return this.camera && this.camera.detectionCapabilities
        ? this.camera.detectionCapabilities.videoMotion
        : null;
    },
    supportsDoorbell() {
      return this.camera && this.camera.detectionCapabilities
        ? this.camera.detectionCapabilities.doorbell
        : null;
    },
    supportsDewarping() {
      return this.camera && this.camera.cameraCapabilities
        ? this.camera.cameraCapabilities.dewarping
        : null;
    },
    canZoomOut() {
      // return this.timeSpan < this.timeSpans[this.timeSpans.length - 1]
      return this.timeLevel < 4;
    },
    canZoomIn() {
      // return this.timeSpan > this.timeSpans[0]
      return this.timeLevel > 1;
    },
    zoomLevel() {
      return Math.round(this.timeSpan / 12);
    },
    currentRecording() {
      const ct = this.currentTime;
      return this.recordings.find(r => {
        return r.closed
          ? new Date(r.startTime).getTime() <= ct &&
              ct <= new Date(r.endTime).getTime()
          : new Date(r.startTime).getTime() <= ct;
      });
    },
    // currentRecordingChunk () {
    //   const ct = this.currentTime
    //   const crc = this.recordingChunks.find(c => {
    //     return c.closed
    //       ? new Date(c.startTime).getTime() <= ct && ct < new Date(c.endTime).getTime()
    //       : new Date(c.startTime).getTime() <= ct
    //   })
    //   // console.log('currentRecordingChunk',
    //   //   crc && crc.closed ? new Date(crc.startTime).getTime() : null, ct,
    //   //   crc && crc.endTime ? new Date(crc.endTime).getTime() : null)
    //   return crc
    // },
    currentSnapshotEvent() {
      const ct = this.currentTime;
      return this.events.find(e => {
        const t = new Date(e.timestamp).getTime();
        return this.isSnapshotEvent(e) && t - 15000 < ct && t + 15000 > ct;
      });
    },
    offset() {
      const offsetToUTC = new Date().getTimezoneOffset()
      return (this.camera.timeOffset - offsetToUTC) * 60000
    },
    travelSpeed() {
      return `${this.$t("Travel speed")}: ${Math.round(this.qrCodeInfo.speed)} km/h, ${Math.round(this.qrCodeInfo.speed*0.621371)} mph`
    },
    mapTooltipText() {
      return (!this.cameraCoordinates.latitude || !this.cameraCoordinates.latitude ) && !this.showMap && this.currentRecording 
        ? this.$t("No GPS Data")
        : this.currentRecording
          ? this.showMap 
            ? this.$t("Close Map") 
            : this.$t("Open Map")
          : this.showMap 
        ? this.$t("Close Map") 
          : this.$t("Please select a valid recording")
    },
    playerHasAudioTrack() { // used for BWC only because they don't have audio detection to determine if it should have audio controls
      return this.player && this.camera?.cameraCapabilities?.recordAudio
      // the below line does only work on experimental web features, and should be used whenever it works on all browsers
      // return !!this.player?.audioTracks().length
    },
    dewarpVolumeIsLow() {
      return !!(this.dewarpVolume < 60)
    }
  },
  watch: {
    locale: function(){ 
     document.getElementById("go-live").innerHTML = `<div>${this.$t("Live")}</div>`; 
     if(!this.timelineInteracted) {
        const dt =  new Date(Date.now());
        this.displayDate = `${dt.toLocaleDateString(
        this.$locale.getCurrentLanguage(),
        { weekday: "long" }
      )}, ${dt.getFullYear()}/${pad(dt.getMonth() + 1)}/${pad(dt.getDate())}`;
       }
    },
    audioAvailable: function(newVal, oldVal) {
      if (!this.supportsDewarping) {
        newVal ? this.player.controlBar.volumePanel.show() : this.player.controlBar.volumePanel.hide()
      }
    },
    currentRecording: function(newVal, oldVal) {
      if (!newVal) {
        this.selectedRecordingChunk = null;
      } 
    },
    showMap: function(newVal, oldVal) {
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 50);
    },
    codec: function(newVal, oldVal) {
      if (newVal !== oldVal && oldVal === "mjpeg") {
        this.$nextTick(() => {
          this.createVideoControlButtons();
        });
      }
    },
    "camera.snapshot": function(newVal, oldVal) {
      if (oldVal !== newVal && this.camera.snapshot && this.$refs.videoPlayer) {
        this.previousSnapshot = this.camera.snapshot;
        if (this.player && !this.supportsDewarping) {
          this.player.poster(this.camera.snapshot);
        }
      }
    },
    userId: function(newVal, oldVal) {
      if (!oldVal && newVal) {
        this.setupStompClient();
      }
    },
    "stompClient.connected": function(newVal, oldVal) {
      console.log("Stomp client connected?", oldVal, "=>", newVal);
      if (oldVal === true && newVal === false) {
        if (this.userId) this.setupStompClient();
      }
    },
    fromTime() {
      const time = this.currentTime - this.offset;
      const dt = new Date(time);
      this.date = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`
      this.displayDate = `${dt.toLocaleDateString(
        this.$locale.getCurrentLanguage(),
        { weekday: "long" }
      )}, ${dt.getFullYear()}/${pad(dt.getMonth() + 1)}/${pad(dt.getDate())}`;
    },
    selectedStartTime: function(newVal, oldVal) {
      if (this.useRecordingChunks && !this.forcePausePlayback) {
        if (this.selectedRecordingChunk) {
          const ts = new Date(this.selectedRecordingChunk.startTime).getTime();
          const sst = new Date(this.selectedStartTime).getTime();
          const seekTime = (sst - ts) / 1000;

          if (this.supportsDewarping) {
            this.immervisionPlayer.video_html.currentTime = seekTime
            if (!this.isDragging) this.immervisionPlayer.video_html.play().catch(() => {})
          }
          if (this.player) {
            this.setPlayerCurrentTime(seekTime)
            if (!this.isDragging) this.player.play().catch(() => {})
          }
        }
      }
    },
    mp4Url: function(newVal, oldVal) {
      if (this.player) {
         //temporary fix for the playbackrates disappearing on the videojs controlbar after dragging the timeline
        this.player.playbackRates(this.playerOptions.playbackRates)
        if (this.mp4Url) {
          if (oldVal !== newVal) {
            this.player.pause();
            this.player.src({
              type: "video/mp4",
              src: this.mp4Url
            });
            this.showSpinnerLoader();
            let seekTime = 0;
            if (this.useRecordingChunks) {
              if (this.selectedRecordingChunk) {
                const ts = new Date(
                  this.selectedRecordingChunk.startTime
                ).getTime();
                const sst = new Date(this.selectedStartTime).getTime();
                seekTime = (sst - ts) / 1000;
              }
            } else {
              if (this.selectedRecording) {
                const ts = new Date(this.selectedRecording.startTime).getTime();
                const sst = new Date(this.selectedStartTime).getTime();
                seekTime = (sst - ts) / 1000;
              }
            }
            const player = this.player;
            // const isPlaying = !this.player.paused()
            const playbackRate = this.playbackRate;
            try {
              player.pause();
            } catch (error) {
              console.error(error);
            }

            if (
              !this.forcePausePlayback &&
              !this.firstPlay &&
              !this.isDragging
            ) {
              // We are getting some errors in Chrome and therefore the timeout
              setTimeout(function() {
                player
                  .play()
                  .then(() => {
                    player.currentTime(seekTime);
                  })
                  .catch(error => {
                    console.error(error);
                  });
              }, 150);
              // We are getting some errors in Chrome and therefore the timeout
              setTimeout(function() {
                player
                  .play()
                  .then(() => {
                    player.playbackRate(playbackRate);
                  })
                  .catch(error => {
                    console.error(error);
                  });
              }, 150);
            }
          }
        } else {
          const playbackRate = this.playbackRate;
          this.player.reset();
          this.player.playbackRate(playbackRate);
          this.hideSpinnerLoader();
        }
      } 
      if (this.supportsDewarping) {
        const playbackRate = this.immervisionPlayer ? this.immervisionPlayer.video_html.playbackRate : 1

        if (newVal) {
          this.setupDewarpPlayer(newVal)

          const ts = new Date(this.selectedRecordingChunk.startTime).getTime();
          const sst = new Date(this.selectedStartTime).getTime();
          const seekTime = (sst - ts) / 1000;
          
          const player = this.immervisionPlayer.video_html
          player.playbackRate = playbackRate

          if (
            !this.forcePausePlayback &&
            !this.firstPlay &&
            !this.isDragging
          ) {
            setTimeout(() => {
              player
                .play()
                .then(() => {
                  player.currentTime = seekTime;
                })
                .catch(error => {
                  console.error(error);
                });
            }, 150);
          }
        } else {
          this.destroyDewarpPlayer()          
        }
      } else {
        this.isLoading = true;
        clearInterval(this.mjpegTimeInterval);
        this.mjpegTimeInterval = null;
        const ts = new Date(this.selectedStartTime)
          .toISOString()
          .replace("Z", "+0000");
        this.currentSnapshot = `${restapi.baseURL}/rest/v2.4/cameras/${
          this.cameraId
        }/snapshot?timestamp=${encodeURIComponent(ts)}&access_token=${
          this.token
        }`;
      }
    },
    currentTime: function(newVal, oldVal) {
      this.autoPlayChunkUpdate = false
      //I added the condition dropdownStatus here to prevent an infinite loop caused by the timepicker
      if (newVal && oldVal !== newVal && !this.dropdownStatus) {
        const dt = new Date(newVal - (this.offset));
        this.displayTime = `${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(
          dt.getSeconds()
        )}`;
        if (oldVal !== null && this.showTimeline) {
          this.loadMoreData();
        }
      }
    },
    recordings: function(newVal, oldVal) {
      // This is a dirty hack to have the right height calculation
      if (this.$refs.videoPlayer)
        this.playerHeight = this.$refs.videoPlayer.clientHeight;
    },
    events: function(newVal, oldVal) {
      // This is a dirty hack to have the right height calculation
      if (this.$refs.videoPlayer)
        this.playerHeight = this.$refs.videoPlayer.clientHeight;
      if (newVal && oldVal !== newVal) this.setPreviousAndNextEvent();
    },
    "this.$refs.videoPlayer.$el.clientHeight"() {
      this.playerHeight = this.$refs.videoPlayer.clientHeight;
    },
    cameraId() {
      this.selectCamera(this.cameraId);
    },
    selectedRecordingChunk: function(newVal, oldVal) {
      if (newVal && oldVal !== newVal) {
        this.setCameraSnapshot(
          newVal.hasOwnProperty("closed") && newVal.closed
            ? newVal.endTime
            : null
        );
      }
    },
    dateDialog: function(newVal, oldVal) {
      if (newVal && oldVal !== newVal) {
        if (this.player) this.player.pause();
        else if (this.immervisionPlayer) this.immervisionPlayer.video_html.pause()
      }
    },
    dropdownStatus: function(newVal, oldVal) {
      if (newVal && oldVal !== newVal) {
        if (this.player) this.player.pause();
        else if (this.immervisionPlayer) this.immervisionPlayer.video_html.pause()
      }
    }
  },
  created() {
    this.timeSpan = this.timeSpans[0];
    this.requestId = Math.round(Math.random() * 100000000);
  },
  async mounted() {
    this.updateRecordingInterval = setInterval(() => {
      this.updateLatestRecordingChunks()
      this.updateOpenRecordings()
    }, 15000)
    Sentry.configureScope(scope => scope.setTransactionName(this.$options.name));

    this.currentTime = store.getters.footageTime
      ? parseInt(store.getters.footageTime)
      : new Date().getTime() - 10 * this.minute;
    store.dispatch("setFootageTime", null);   

    this.timeLevel = store.getters.footageZoom
      ? parseInt(store.getters.footageZoom)
      : this.timeLevel;
    store.dispatch("setFootageZoom", null);
    
    this.updateTimeLevel();
    
    this.onResize();

    this.calculateEventFilter()
    
    const dt = new Date(this.currentTime -  this.offset);

    this.displayDate = `${dt.toLocaleDateString(
        this.$locale.getCurrentLanguage(),
        { weekday: "long" }
      )}, ${dt.getFullYear()}/${pad(dt.getMonth() + 1)}/${pad(dt.getDate())}`;
      
    this.timeAsHMS = {
      HH: pad(dt.getHours()),
      mm: pad(dt.getMinutes()),
      ss: pad(dt.getSeconds())
    };

    if (this.userId) this.setupStompClient();

    if (this.camera) { 
      this.loadCameraData()

      if (this.currentRecording) {
        this.togglePlay();
      } else if (this.recordings.length) {
        this.selectNearestRecording(this.currentTime);
      }

      this.offsetStamp = getOffsetStamp(this.camera.timeOffset, this.accountTimeZoneOffset)

      if (this.codec !== "mjpeg") {
        this.player = videojs( // on dewarp we use the video.js for audio only, without controls
          this.supportsDewarping ? this.$refs.dewarpAudioPlayer : this.$refs.videoPlayer,
          this.supportsDewarping ? { muted: true } : this.playerOptions,
          function onPlayerReady() {
            console.log("onPlayerReady", this);
          }
        );

        if (!this.supportsDewarping) {  // dont let the dedicated audio player trigger any events
          this.player.on("timeupdate", this.onPlayerStateChanged);
          this.player.on("waiting", this.onPlayerStateChanged);
          this.player.on("play", this.onPlayerStateChanged);
          this.player.on("pause", this.onPlayerStateChanged);
          this.player.on("ended", this.onPlayerStateChanged);
          this.player.on("firstplay", this.onPlayerStateChanged);
          this.player.on("ended", this.onPlayerEnded);
          this.player.on("timeupdate", this.onPlayerTimeupdate);
          this.player.on("ratechange", this.onRateChange);
          this.player.on("error", this.onVideojsError)
        }
      }

      if (this.codec === 'mjpeg') {
        const ts = new Date(this.currentTime).toISOString().replace("Z", "+0000");
        this.currentSnapshot = `${restapi.baseURL}/rest/v2.4/cameras/${
          this.cameraId
        }/snapshot?timestamp=${encodeURIComponent(ts)}&access_token=${
          this.token
        }`;
      } else {
        this.createVideoControlButtons();
      }
    }
    window.addEventListener("mousemove", this.mouseMoveListen)
    window.addEventListener("resize", this.onResize)
  },
  beforeDestroy() {
    console.log("Footage - Now in beforeDestroy");
    this.footageShouldDestroy = true;
    // restapi.cancelAll(); this was interferring with detectioncapabilities request
    clearInterval(this.updateRecordingInterval)
    if (this.getEventInterval) {
      console.log("Clearing getEventInterval");
      clearInterval(this.getEventInterval);
      this.getEventInterval = null;
    } else {
      console.log("No getEventInterval");
    }
    if (this.player) {
      this.player.off([
        "timeupdate",
        "waiting",
        "play",
        "pause",
        "ended",
        "firstplay",
        "ended",
        "timeupdate",
        "ratechange"
      ]);
      this.player.dispose();
    }
    if (this.stompClient && this.stompClient.connected) {
      this.stompClient.disconnect();
    }
    window.removeEventListener("mousemove", this.mouseMoveListen)
    window.removeEventListener("resize", this.onResize)
  },
  methods: {
    getPlayerCurrentTime() {
      return this.immervisionPlayer ? this.immervisionPlayer.video_html.currentTime : this.player.currentTime()
    },
    setPlayerCurrentTime(newValue) {
      if (this.immervisionPlayer) 
        this.immervisionPlayer.video_html.currentTime = newValue
      if (this.player)
        this.player.currentTime(newValue)
    },
    getPlayerDuration() {
      return this.immervisionPlayer ? this.immervisionPlayer.video_html.duration : this.player.duration()
    },
    getTranslatedEventType(event) {
      const eventType = this.eventTypes[event.type]
      if (event.category && eventType.categoriesTranslated && eventType.categoriesTranslated[event.category]) {
        return eventType.categoriesTranslated[event.category]
      } else {
        return eventType ? eventType.eventTypeTranslated : this.$t(event.type)
      }
    },
    setupDewarpPlayer(mp4 = '') {
      try {
        const container = this.$refs.dewarpingContainer

        const options = {
          video: mp4,
          orientation: "WALL",
          onVideoFrame: () => { 
            this.onPlayerTimeupdate()
            this.updateBodycamRotation() 
          },
          onStateChange: (newState, state) => {
            console.log("DOM.onStateChange:", newState)
          },
          onQrCode: this.onQrCode
        }
  
        if (this.immervisionPlayer) {
          this.immervisionPlayer.setState(options)
        } else {
          this.immervisionPlayer = new window.immervisionSDK.IVEViewer({target: container}, options);
          this.immervisionPlayer.view.zoom(2.8)
        }

        this.immervisionPlayer.video_html.addEventListener("ended", this.onPlayerEnded)

        this.immervisionPlayer.video_html.addEventListener("play", this.onPlayerStateChanged)
        this.immervisionPlayer.video_html.addEventListener("pause", this.onPlayerStateChanged)
        this.immervisionPlayer.video_html.addEventListener("ended", this.onPlayerStateChanged)
    
        this.immervisionPlayer.video_html.crossOrigin = "anonymous"
        this.immervisionPlayer.video_html.loop = false

        if (!this.immervisionControl) {
          this.immervisionControl = new window.immervisionSDK.Control(container, {
            onZoom: this.immervisionPlayer.view.zoom,
            onRotate: this.rotateDewarp,
          })
        }
      } catch (error) {
        console.error(error)
      }
    },
    destroyDewarpPlayer() {
      this.immervisionPlayer.video_html.removeEventListener("ended", this.onPlayerEnded)

      this.immervisionPlayer.video_html.removeEventListener("play", this.onPlayerStateChanged)
      this.immervisionPlayer.video_html.removeEventListener("pause", this.onPlayerStateChanged)
      this.immervisionPlayer.video_html.removeEventListener("ended", this.onPlayerStateChanged)

      this.immervisionPlayer.video_html.pause()
      this.immervisionPlayer.video_html.removeAttribute('src')
      this.immervisionPlayer.video_html.load()

      this.immervisionPlayer.destroy();

      this.immervisionPlayer = null;
      this.immervisionControl = null;
    },
    onQrCode(qrCodeString) {
      this.qrCodeInfo = parseQrCode(qrCodeString);
      if(this.qrCodeInfo.latitude && this.qrCodeInfo.longitude)
        this.gpsDetected = true;
    },
    toggleMapView() {
      this.showMap = !this.showMap;
    },
    toggleDewarpFullscreen() {
      fullscreen.toggle(this.$refs["dewarp-fullscreen"], {
        callback: (isFullscreen) => {
          this.dewarpFullscreen = isFullscreen
        },
      })
    },
    closeTimePicker() {
      if (this.$refs.timepicker && this.dropdownStatus) {
        this.$refs.timepicker.toggleActive();
      }
    },
    removeVideoSource() {
      document.getElementsByClassName("vjs-tech")[0].src = "";
    },
    showSpinnerLoader() {
      document.getElementsByClassName("vjs-loading-spinner")[0].style.display =
        "block";
    },
    hideSpinnerLoader() {
      document.getElementsByClassName("vjs-loading-spinner")[0].style.display =
        "none";
    },
    dragStart() {
      this.pauseVideo();
      this.isDragging = true;
    },
    dragEnd() {
      this.isDragging = false;
    },
    imgLoaded() {
      const that = this;
      function addTime() {
        if (that.isPlaying && !that.isLoading) {
          that.currentTime += 100;
        }
      }
      if (this.isLoading) {
        this.isLoading = false;
        this.isPlaying = true;
        this.morphSVG();
        clearInterval(this.mjpegTimeInterval);
        this.mjpegTimeInterval = null;
        this.mjpegTimeInterval = setInterval(addTime, 100);
      }
    },
    goLive() {
      this.$cookies.set("grid",1);
      this.$router.push({ name: "Live", params: { cameraId: this.cameraId } });
    },
    backwardsTimeJump() {
      this.pauseVideo();
      this.dragTimeline(-10000);
    },
    forwardTimeJump() {
      this.pauseVideo();
      this.dragTimeline(10000);
    },
    createVideoControlButtons() {
      let that = this;
      let snapshot = () => this.takeSnapshot();
      let back10 = () => this.backwardsTimeJump();
      let forward10 = () => this.forwardTimeJump();
      let goLive = () => this.goLive();

      if (this.$refs.videoPlayer) {
        let button = videojs.getComponent("Button");
        let snapshotButton = videojs.extend(button, {
          constructor: function() {
            button.apply(this, arguments);
            this.controlText(that.$t("Take Snapshot"));
            this.addClass("icon-snapshot");
          },
          handleClick: function() {
            snapshot();
          }
        });
        videojs.registerComponent("snapshotButton", snapshotButton);
        this.player.getChild("controlBar").addChild("snapshotButton", {});

        let backButton = videojs.extend(button, {
          constructor: function() {
            button.apply(this, arguments);
            this.controlText(that.$t("Go back 10 seconds"));
            this.addClass("icon-time-jump");
            this.addClass("flip");
          },
          handleClick: function() {
            back10();
          }
        });
        videojs.registerComponent("backButton", backButton);
        this.player.getChild("controlBar").addChild("backButton", {});

        let forwardButton = videojs.extend(button, {
          constructor: function() {
            button.apply(this, arguments);
            this.controlText(that.$t("Advance 10 seconds"));
            this.addClass("icon-time-jump");
          },
          handleClick: function() {
            forward10();
          }
        });
        videojs.registerComponent("forwardButton", forwardButton);
        this.player.getChild("controlBar").addChild("forwardButton", {});

        if (!this.hideGoLive) {
          let innerHTML = `<div>${this.$t("Live")}</div>`;

          let liveButton = videojs.extend(button, {
            constructor: function() {
              button.apply(this, arguments);
            },
            handleClick: function() {
              goLive();
            },
            createEl: function() {
              let el;

              el = videojs.createEl("button", {
                className: "go-live",
                id: "go-live",
                innerHTML: innerHTML
              });

              return el;
            }
          });
          videojs.registerComponent("liveButton", liveButton);
          this.player.getChild("controlBar").addChild("liveButton", {});
        }
      }
    },
    async takeSnapshot() {
      this.openSnapshot = !this.openSnapshot
    },
    mouseMoveListen() {
      this.mouseMoved = true;
      clearTimeout(this.mouseMovedTimeout);
      this.mouseMovedTimeout = setTimeout(() => {
        this.mouseMoved = false;
      }, 2000);
    },
    togglePlay() {
      if (this.codec === "mjpeg") {
        if (this.isPlaying) {
          this.dragTimeline(0);
          clearInterval(this.mjpegTimeInterval);
          this.mjpegTimeInterval = null;
          this.isPlaying = false;
          this.morphSVG();
        } else {
          if (this.firstPlay) {
            this.recordingSelected(
              this.currentRecording.recordingId,
              this.currentTime
            );
            this.firstPlay = false;
            this.isPlaying = true;
            this.morphSVG();
          } else {
            this.isPlaying = true;
            this.morphSVG();
          }
        }
      } else {
        if (this.isPlaying) {
          this.pauseVideo();
          this.isPlaying = false;
          this.morphSVG();
        } else {
          if (this.firstPlay) {
            this.recordingSelected(
              this.currentRecording.recordingId,
              this.currentTime
            );
            this.firstPlay = false;
          } else {
            this.playVideo();
            this.isPlaying = true;
            this.morphSVG();
          }
        }
      }
    },
    morphSVG() {
      let path = this.$refs.path;
      let anim = this.$refs.animate;

      let path2 = this.$refs.path2;
      let anim2 = this.$refs.animate2;

      let firstPath =
        "M11,8 L18,11.74 18,20.28 11,24 11,8   M18,11.74 L26,16 26,16 18,20.28 18,11.74";
      let secondPath =
        "M9,8  L14,8     14,24    9,24  9,8    M19,8     L24,8  24,24 19,24    19,8";

      if (this.isPlaying) {
        path.setAttribute("d", secondPath);
        anim.setAttribute("from", firstPath);
        anim.setAttribute("to", secondPath);
        if (this.supportsDewarping || this.codec === "mjpeg") {
          path2.setAttribute("d", secondPath);
          anim2.setAttribute("from", firstPath);
          anim2.setAttribute("to", secondPath);
        }
      } else {
        path.setAttribute("d", firstPath);
        anim.setAttribute("from", secondPath);
        anim.setAttribute("to", firstPath);
        if (this.supportsDewarping || this.codec === "mjpeg") {
          path2.setAttribute("d", firstPath);
          anim2.setAttribute("from", secondPath);
          anim2.setAttribute("to", firstPath);
        }
      }
      anim.beginElement();
      if (this.supportsDewarping || this.codec === "mjpeg") {
        anim2.beginElement();
      }
    },
    openDebugTools() {
      this.debugTools = true;
    },
    updateTimeLevel() {
      if (this.timeLevel === 1) {
        this.timing = this.minute;
      } else if (this.timeLevel === 2) {
        this.timing = this.minute * 15;
      } else if (this.timeLevel === 3) {
        this.timing = this.hour;
      } else if (this.timeLevel === 4) {
        this.timing = this.hour * 3;
      }
    },
    onDraggingTimeUpdate(time) {
      this.timelineInteracted = true
      const dt = new Date(time);
      this.date = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`
      window.addEventListener("mouseup", this.removePoster);
      this.displayTime = `${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;
      this.displayDate = `${dt.toLocaleDateString(
        this.$locale.getCurrentLanguage(),
        { weekday: "long" }
      )}, ${dt.getFullYear()}/${pad(dt.getMonth() + 1)}/${pad(dt.getDate())}`;
    },
    removePoster() {
      if (this.player && !this.supportsDewarping) {
        this.player.poster(null);
        window.removeEventListener("mouseup", this.removePoster);
      }
    },
    playVideo() {
      if (this.supportsDewarping && this.immervisionPlayer) this.immervisionPlayer.video_html.play()
      if (this.player) this.player.play()
    },
    pauseVideo() {
      if (this.supportsDewarping && this.immervisionPlayer) this.immervisionPlayer.video_html.pause()
      if (this.player) this.player.pause()
    },
    updateBodycamRotation() {
      if(this.immervisionPlayer) {
        this.immervisionPlayer.setState({
          rotation: {
            x: 0,
            y: 0,
            z: 0
          }
        })
      }
    },
    onResize() {
      this.shouldCollapseButtons = this.$refs.test
        ? this.$refs.test.clientWidth < 1500
        : false;
      this.playerHeight = this.$refs.videoPlayer
        ? this.$refs.videoPlayer.clientHeight
        : null;
      if (this.$refs.timeline)
        setTimeout(this.$refs.timeline.updateScrollPos, 100);
    },
    handleRotationInput(direction) {
      switch (direction) {
        case 'right':
          this.rotateDewarp(0, 0.05, false)
          break;
        case 'up':
          this.rotateDewarp(-0.05, 0, false)
          break;
        case 'left':
          this.rotateDewarp(0, -0.05, false)
          break;
        case 'down':
          this.rotateDewarp(0.05, 0, false)
          break;
      }
    },
    rotateDewarp(x, y, inverted = true) {
      const finalX = inverted ? -x : x
      const finalY = inverted ? -y : y
      this.immervisionPlayer.view.rotate(finalX, finalY)
    },
    selectCamera(id) {
      this.$emit("select", id || parseInt(id));
    },
    dateHeader(date) {
      date = date || this.currentTime;
      if (
        this.$moment(date)
          .startOf("day")
          .format("YYYY/MM/DD") ===
        this.$moment()
          .startOf("day")
          .format("YYYY/MM/DD")
      )
        return this.$t("Today");
      if (
        this.$moment(date)
          .startOf("day")
          .format("YYYY/MM/DD") ===
        this.$moment()
          .add(-1, "days")
          .startOf("day")
          .format("YYYY/MM/DD")
      )
        return this.$t("Yesterday");
      return this.$moment(date).format("YYYY/MM/DD");
    },
    changedTime() {
      const dt = new Date(this.currentTime);
      dt.setHours(parseInt(this.timeAsHMS.HH));
      dt.setMinutes(parseInt(this.timeAsHMS.mm));
      dt.setSeconds(parseInt(this.timeAsHMS.ss));
      this.currentTime = dt.getTime();
    },
    setNewTime() {
      this.timeDialog = false;
      const dt = new Date(this.currentTime);
      dt.setHours(this.newTime.hours);
      dt.setMinutes(this.newTime.minutes);
      dt.setSeconds(0);
      this.currentTime = dt.getTime();
    },
    shortDate(timeString) {
      const ts = Date.parse(timeString);
      const dt = new Date(ts);
      return `${dt.getFullYear()}-${dt.getMonth() + 1}-${dt.getDate()}`;
    },
    shortDateTime(timeString) {
      const ts = Date.parse(timeString);
      const dt = new Date(ts);
      return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(
        dt.getDate()
      )} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
    },
    eventTime(event) {
      const dt = new Date(event.timestamp);
      return `${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(
        dt.getSeconds()
      )}`;
    },
    dragTimeline(time) {
      this.selectedEvent = null;
      if (time > 0) {
        this.currentTime = Math.min(
          this.currentTime + time,
          new Date().getTime()
        );
      } else {
        const times = [this.currentTime + time];
        if (this.firstEvent)
          times.push(new Date(this.firstEvent.timestamp).getTime());
        if (this.firstRecording)
          times.push(new Date(this.firstRecording.startTime).getTime());
        this.currentTime = Math.max(
          this.currentTime + time,
          Math.min(...times)
        );
      }
      if (this.currentRecording) {
        this.recordingSelected(
          this.currentRecording.recordingId,
          this.currentTime
        );
      }
    },
    dateSelected() {
      this.selectedEvent = null;
      this.dateDialog = false;
      this.currentTime = new Date(`${this.date}T12:00`).getTime() + this.offset;
      if (this.currentRecording)
        this.recordingSelected(
          this.currentRecording.recordingId,
          this.currentTime
        );
    },
    timeSelected(eventData) {
      if (this.dropdownStatus) {
        this.selectedEvent = null;
        this.timeDialog = false;
        this.currentTime = new Date(`${this.date}T${eventData.displayTime}`).getTime() + this.offset;
        if (this.currentRecording)
          this.recordingSelected(
            this.currentRecording.recordingId,
            this.currentTime
          );
      }
    },
    saveTime(time) {
      this.timeDialog = false;
      console.log("Saving time", time);
    },
    zoomOut() {
      this.timeSpan = this.timeSpan * 2;
      this.timeLevel++;
      this.updateTimeLevel();
    },
    zoomIn() {
      this.timeSpan = this.timeSpan / 2;
      this.timeLevel--;
      this.updateTimeLevel();
    },
    setTimeSpan(min) {
      this.timeSpan = min;
    },
    selectNearestRecording(time) {
      let decimals = Math.floor(Math.random() * (1000 - 100) + 100) / 1000;
      let recording = this.recordings.sort((a, b) => {
        return (
          Math.abs(time - new Date(a.startTime).getTime()) -
          Math.abs(time - new Date(b.startTime).getTime())
        );
      });
      if (
        Math.abs(time - new Date(recording[0].startTime).getTime()) <
        this.minute
      ) {
        this.recordingSelected(
          recording[0].recordingId,
          new Date(recording[0].startTime).getTime() + decimals
        );
      }
    },
    eventSelected(id) {
      //  this.selectedRecording = null
      let selectedEvent = this.events.find(e => {
        return e.eventId === id;
      });
      let eventTime = new Date(selectedEvent.timestamp).getTime();
      let decimals = Math.floor(Math.random() * (1000 - 100) + 100) / 1000;
      this.currentTime = eventTime - 3000;
      if (!this.currentRecording) {
        this.selectNearestRecording(eventTime);
      } else {
        this.recordingSelected(
          this.currentRecording.recordingId,
          eventTime - 3000 + decimals
        );
      }
    },
    recordingSelected(id, time) {
      this.firstPlay = false;
      this.selectedStartTime = time;
      const recordingId = this.selectedRecording
        ? this.selectedRecording.recordingId
        : -1;
      this.selectedEvent = null;
      this.selectedRecording = this.recordings.find(r => {
        return r.recordingId === id;
      });
      if (this.useRecordingChunks) {
        if (id && id !== recordingId) {
          const that = this;
          that.recordingChunks = [];
          that.selectedRecordingChunk = null;
          const startTime = new Date(this.selectedRecording.startTime)
            .toISOString()
            .replace("Z", "+0000");
          restapi
            .getCameraRecording(this.cameraId, id, startTime, this.requestId)
            .then(function(chunks) {
              that.recordingChunks = chunks;
              that.currentTime = time;
              that.selectedRecordingChunk = that.recordingChunks.find(c => {
                return c.closed
                  ? new Date(c.startTime).getTime() <= time &&
                      time < new Date(c.endTime).getTime()
                  : time >= new Date(c.startTime).getTime();
              });
            })
            .catch(function(error) {
              console.error(error);
            });
        } else {
          this.currentTime = time;
          this.selectedRecordingChunk = this.recordingChunks.find(c => {
            return c.closed
              ? new Date(c.startTime).getTime() <= time &&
                  time < new Date(c.endTime).getTime()
              : time >= new Date(c.startTime).getTime();
          });
        }
      } else {
        this.currentTime = time;
      }
    },
    downloadRecording(id) {
      const recording = this.recordings.find(r => {
        return r.recordingId === id;
      });
      if (
        recording &&
        recording.urls.hasOwnProperty("mp4Https") &&
        recording.urls.mp4Https
      ) {
        const anchor = document.createElement("a");
        const href = `${recording.urls.mp4Https}?access_token=${this.token}`;
        anchor.href = href;
        anchor.download = `${id}.mp4`;
        document.body.appendChild(anchor);
        // anchor.click()
      }
      return null;
    },
    downloadRecordingLink(id) {
      console.log("Now downloading recording with id", id);
      const recording = this.recordings.find(r => {
        return r.recordingId === id;
      });
      console.log("Found recording", recording);
      if (
        recording &&
        recording.urls.hasOwnProperty("mp4Https") &&
        recording.urls.mp4Https
      ) {
        console.log(recording.urls.mp4Https);
        return recording.urls.mp4Https;
      }
      return null;
    },
    onPlayerReady() {
      this.videoHeight = this.$refs.videoPlayer.$el.clientHeight;
    },
    onRateChange() {
      this.playbackRate = this.player.playbackRate();
      this.player.defaultPlaybackRate(this.player.playbackRate());
    },
    onPlayerTimeupdate() {
      if (this.selectedEvent) {
        let ts = new Date(this.selectedEvent.timestamp);
        ts = ts.setSeconds(
          Math.round(ts.getSeconds() + this.getPlayerCurrentTime())
        );
        this.currentTime = ts;
      } else if (this.selectedRecordingChunk) {
        const ct = this.supportsDewarping && this.immervisionPlayer ? this.immervisionPlayer.video_html.currentTime : this.getPlayerCurrentTime();
        if (ct > 0) {
          // let ts = this.selectedStartTime ? new Date(this.selectedStartTime) : new Date(this.selectedRecordingChunk.startTime)
          // ts = ts.setSeconds(Math.round(ts.getSeconds() + ct))
          // console.log(this.$moment(ts).format('LTS'))
          const ts = new Date(this.selectedRecordingChunk.startTime);
          this.currentTime = ts.getTime() + ct * 1000;
        }
      } else if (this.selectedRecording) {
        const ct = this.getPlayerCurrentTime();
        if (ct > 0) {
          // let ts = this.selectedStartTime ? new Date(this.selectedStartTime) : new Date(this.selectedRecording.startTime)
          // ts = ts.setSeconds(Math.round(ts.getSeconds() + player.currentTime()))
          const ts = new Date(this.selectedRecording.startTime);
          this.currentTime = ts.getTime() + ct * 1000;
        }
      } else if (this.currentRecording) {
        // if (!this.currentRecordingStartTime) this.currentRecordingStartTime = this.currentTime
        // let ts = new Date(this.currentRecordingStartTime)
        // console.log(ts, player.currentTime(), this.currentTime)
        // ts = ts.setSeconds(Math.round(ts.getSeconds() + player.currentTime()))
        // this.currentTime = ts
      }
    },
    onPlayerEnded() {
      this.selectedStartTime = null;
      if (this.selectedRecordingChunk) {
        const et = new Date(this.selectedRecordingChunk.endTime).getTime();
        const nextChunk = this.recordingChunks.find(c => {
          return new Date(c.startTime).getTime() === et;
        });
        this.selectedRecordingChunk = null;
        if (nextChunk)
          this.recordingSelected(this.currentRecording.recordingId, et);
        else {
          const nextRecordingId = this.recordings.findIndex(
            rec => rec.recordingId === this.currentRecording.recordingId
          );
          if (nextRecordingId) {
            const recording = this.recordings[nextRecordingId - 1];
            this.recordingSelected(
              recording.recordingId,
              new Date(recording.startTime).getTime()
            );
          } else {
            this.autoPlayChunkUpdate = true
            this.updateLatestRecordingChunks()
          }
        }
      }
    },
    onPlayerStateChanged(data) {
      const player = { [data.type]: true };
      if (player.timeupdate) {
        this.isBufferingVideo = false;
      }
      if (player.waiting) {
        this.isBufferingVideo = true;
      }
      if (player.play || player.firstplay) {
        clearTimeout(this.playButtonTimeout);
        this.playButtonTimeout = null;
        this.isPlaying = true;
        this.morphSVG();
      }
      if (player.pause || player.ended) {
        if (!this.playButtonTimeout) {
          this.playButtonTimeout = setTimeout(() => {
            if (this.isPlaying) {
              this.isPlaying = false;
              this.morphSVG();
            }
          }, 100);
        }
      }
    },
    setupStompClient() {
      if (this.token) {
        console.log("Setting up stomp client");
        /* eslint-disable no-undef */
        const url =
          restapi.baseURL.replace("http", "ws") +
          "/stomp/v2.0?access_token=" +
          this.token;
        this.stompClient = new StompClient(
          url,
          this.userId,
          null,
          null,
          this.notify
        );
        this.stompClient.connect();
      }
    },
    notify(tick) {
      const routing = tick.headers.destination.split('/')[3].split('.')
      const message = JSON.parse(tick.body)
      if (routing[1] === 'cameras' && this.camera && message.cameraId === this.camera.cameraId) {
        if (routing[3] === 'events_v24') {
            if (this.eventTypesNames.includes(message.type)) {
              const that = this;
              return restapi
                .getEvent(message.eventId)
                .then(function(event) {
                  console.log("Received event", event);
                  if (that.events === null || !Array.isArray(that.events))
                    that.events = [];
                  let hash = that.hashEvents;
                  hash[event.eventId] = event;
                  that.events = Object.values(hash);
                  that.setPreviousAndNextEvent();
                })
                .catch(function(error) {
                  console.error(error);
                })
                .then(function() {
                  return true;
                });
            }
        } else if (routing[3] === 'recordings') {
          return restapi
            .getCameraRecording(
              message.cameraId,
              message.recordingId,
              null,
              this.requestId
            )
            .then(recording => {
              console.log("Received recording", recording);
              let hash = this.hashRecordings;
              hash[recording.recordingId] = recording;
              this.recordings = Object.values(hash);
              this.sortRecordings();
            });
        }
      }
    },
    firstRecordingDate(camera) {
      if (camera && camera.subscriptionRights) {
        const dt = new Date();
        dt.setDate(dt.getDate() - camera.subscriptionRights.daysOfRecordings);
        return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(
          dt.getDate()
        )}`;
      }
      return 0;
    },
    mergeRecordings(recordings) {
      for (let i = 0; i < recordings.length; i++) {
        if (
          !this.recordings.find(r => {
            return r.recordingId === recordings[i].recordingId;
          })
        ) {
          this.recordings.push(recordings[i]);
        }
      }
    },
    setPreviousAndNextEvent(shouldSecondPreviousEvent = false) {
      if (this.filteredEvents && this.filteredEvents.length > 0) {
        const sortedEvents = this.filteredEvents
          ? this.filteredEvents.sort(function(a, b) {
              return (
                new Date(b.timestamp).getTime() -
                new Date(a.timestamp).getTime()
              );
            })
          : [];
        let events = sortedEvents.filter(e => {
          return new Date(e.timestamp).getTime() < this.currentTime + 1000;
        });
        this.previousEvent = events.length > 0 ? events[0] : null;
        this.secondPreviousEvent =
          events.length > 0
            ? shouldSecondPreviousEvent
              ? events[1]
              : events[0]
            : null;
        events = sortedEvents.filter(e => {
          return new Date(e.timestamp).getTime() > this.currentTime + 3000;
        });
        this.nextEvent = events.length > 0 ? events[events.length - 1] : null;
      } else {
        this.previousEvent = this.nextEvent = null;
      }
    },
    gotoPreviousEvent() {
      if (this.secondPreviousEvent) {
        this.eventSelected(this.secondPreviousEvent.eventId);
        this.setPreviousAndNextEvent(false);
      } else {
        this.setPreviousAndNextEvent(true);
        this.eventSelected(this.previousEvent.eventId);
      }
      clearTimeout(this.secondPreviousEventTimer);
      this.secondPreviousEventTimer = setTimeout(() => {
        this.secondPreviousEvent = null;
      }, 2000);
    },
    gotoNextEvent() {
      this.setPreviousAndNextEvent(false);
      this.eventSelected(this.nextEvent.eventId);
    },
    showPreviousFrame() {
      if (this.player || this.immervisionPlayer) {
        if (this.isPlaying) {
          this.pauseVideo();
        }
        let ts = this.getPlayerCurrentTime();
        ts -= Math.round((1 / this.framerate) * 1000) / 1000;
        ts = Math.round(ts * 1000) / 1000;
        if (ts >= 0) {
          this.setPlayerCurrentTime(ts);
        } else {
          // this.printVariables('showPrevFrame - start');
          this.selectedStartTime = null;
          if (this.selectedRecordingChunk) {
            console.log(
              "Now looking for chunk which ends at",
              new Date(
                this.selectedRecordingChunk.startTime
              ).toLocaleTimeString()
            );
            const st = new Date(
              this.selectedRecordingChunk.startTime
            ).getTime();
            const previousChunk = this.recordingChunks.find(c => {
              return new Date(c.endTime).getTime() === st;
            });
            this.setPlayerCurrentTime(0);
            this.selectedRecordingChunk = null;
            this.forcePausePlayback = true;
            if (previousChunk) {
              console.log(
                "Found previous chunk",
                new Date(previousChunk.startTime).toLocaleTimeString(),
                "=>",
                new Date(previousChunk.endTime).toLocaleTimeString()
              );
              const chunkEndTime =
                new Date(previousChunk.endTime).getTime() - 1000;
              this.recordingSelected(
                this.currentRecording.recordingId,
                chunkEndTime
              );
            } else {
              const previousRecording = this.recordings.find(r => {
                return new Date(r.endTime).getTime() === st;
              });
              if (previousRecording) {
                console.log("Found previous recording", previousRecording);
                console.log(previousRecording);
                this.recordingSelected(previousRecording.recordingId, st);
              } else {
                console.log("Previous recording not found");
              }
            }
            const that = this;
            setTimeout(function() {
              that.forcePausePlayback = false;
              // that.printVariables('showPrevFrame - end')
            }, 1000);
          }
        }
      }
    },
    printVariables(name) {
      console.log("==============================");
      console.log("=== " + name + " ===");
      console.log(
        "this.currentTime: " + new Date(this.currentTime).toISOString()
      );
      console.log(
        "this.selectedRecordingChunk: " +
          this.selectedRecordingChunk.startTime +
          " => " +
          this.selectedRecordingChunk.endTime +
          ", closed: " +
          this.selectedRecordingChunk.closed
      );
      console.log(
        "this.selectedStartTime: " +
          new Date(this.selectedStartTime).toISOString()
      );
      console.log(
        "this.selectedRecording: " +
          this.selectedRecording.startTime +
          ", closed: " +
          this.selectedRecording.closed
      );
      console.log("==============================");
    },
    showNextFrame() {
      if (this.player || this.immervisionPlayer) {
        if (this.isPlaying) {
          this.pauseVideo();
        }
        let ts = this.getPlayerCurrentTime();
        ts += Math.round((1 / this.framerate) * 1000) / 1000;
        ts = Math.round(ts * 1000) / 1000;
        if (ts < this.getPlayerDuration()) {
          this.setPlayerCurrentTime(ts);
        } else {
          // this.printVariables('showNextFrame - start');
          this.selectedStartTime = null;
          if (this.selectedRecordingChunk) {
            const et = new Date(this.selectedRecordingChunk.endTime).getTime();
            const nextChunk = this.recordingChunks.find(c => {
              return new Date(c.startTime).getTime() === et;
            });
            this.setPlayerCurrentTime(0);
            this.selectedRecordingChunk = null;
            this.forcePausePlayback = true;
            if (nextChunk) {
              this.recordingSelected(this.currentRecording.recordingId, et);
            } else {
              const nextRecording = this.recordings.find(r => {
                return new Date(r.startTime).getTime() === et;
              });
              if (nextRecording) {
                this.recordingSelected(nextRecording.recordingId, et);
              }
            }
            const that = this;
            setTimeout(function() {
              that.forcePausePlayback = false;
              // that.printVariables('showNextFrame - end')
            }, 1000);
          }
        }
      }
    },
    filterClicked(type) {
      const index = this.selectedEventFilters.indexOf(type);
      if (index > -1) {
        this.selectedEventFilters.splice(index, 1);
      } else {
        this.selectedEventFilters.push(type);
      }
    },
    filterTextColor(type) {
      return this.selectedEventFilters.includes(type)
        ? "primary"
        : "#e0e0e0";
    },
    hoverStart(eventId, event) {
      if (!this.hoveredEvent) {
        this.hoveredEvent = this.events.find(e => {
          return e.eventId === parseInt(eventId);
        });
        const x = event.pageX;
        const y = event.pageY;
        const hover = this.$refs.eventTooltip;
        hover.$el.style.left = Math.max(0, x - 125) + "px";
        hover.$el.style.top = Math.max(0, y - 225) + "px";
        hover.$el.style.visibility = "visible";
        // if (!this.hoveredEvent.hasOwnProperty('hoverSnapshot')) {
        //   const that = this
        //   let ts = (new Date(this.hoveredEvent.timestamp)).getTime()
        //   // this.hoveredEvent.timestamp.replace('Z', '+0000')
        //   restapi.getSnapshot(this.camera.cameraId, '320x240', ts)
        //     .then(function (snapshot) {
        //       that.$set(that.hoveredEvent, 'hoverSnapshot', snapshot)
        //     })
        // }
      }
    },
    hoverStop(eventId, event) {
      const hover = this.$refs.eventTooltip;
      hover.$el.style.visibility = "hidden";
      this.hoveredEvent = null;
    },
    scrolled(e) {
      console.log("Scrolling...", e.target.scrollTop);
    },
    async loadMoreData() {
      let minDate = this.currentTime - this.timing * 48;
      let maxDate = this.currentTime + this.timing * 48;
      let min =
        minDate > this.minEventDate && minDate < this.maxEventDate
          ? this.maxEventDate
          : minDate;
      let max =
        maxDate < this.maxEventDate && maxDate > this.minEventDate
          ? this.minEventDate
          : maxDate;
      if (
        !this.eventsRetrievalPending &&
        this.camera &&
        Math.abs(min - max) > this.timing * 24
      ) {
        this.minEventDate = minDate;
        this.maxEventDate = maxDate;
        this.eventsRetrievalPending = true;

        let events = this.loadMoreEvents(min, max);
        let recordings = this.loadMoreRecordings(min, max);

        return Promise.all([events, recordings])
          .then(() => {
            this.eventsRetrievalPending = false;
            this.cleanUpEvents(
              minDate - this.timing * 12,
              maxDate + this.timing * 12
            );
            this.cleanUpRecordings(
              minDate - this.timing * 12,
              maxDate + this.timing * 12
            );
            this.sortRecordings();
            this.showTimeline = true;
          })
          .catch(function(error) {
            Sentry.captureException(error)
            console.error(error);
            this.eventsRetrievalPending = false;
            setTimeout(this.loadMoreData, 1000);
          });
      } else {
        return true;
      }
    },
    loadMoreEvents(minTime, maxTime) {
      let hash = this.hashEvents;
      let callback = chunk => {
        chunk.forEach(element => {
          hash[element.eventId] = element;
        });
        this.events = Object.values(hash);
      };

      return restapi.getAllNewCameraEvents(
        this.camera.cameraId,
        minTime,
        maxTime,
        null,
        this.requestId,
        null,
        callback
      );
    },
    loadMoreRecordings(minTime, maxTime) {
      let hash = this.hashRecordings;
      let callback = chunk => {
        chunk.forEach(element => {
          if (
            hash[element.recordingId] !== undefined &&
            hash[element.recordingId].closed !== element.closed &&
            element.closed
          ) {
            let diff = new Date() - new Date(element.endTime);
            if (diff < this.minute * 10) {
              hash[element.recordingId] = element;
            }
          } else {
            hash[element.recordingId] = element;
          }
        });
        this.recordings = Object.values(hash);
      };

      const that = this;
      return restapi
        .getAllCameraRecordings(
          this.camera.cameraId,
          minTime,
          maxTime,
          this.requestId,
          callback
        )
        .then(function(recordings) {
          if (that.useRecordingChunks && that.currentRecording) {
            const startTime = new Date(that.currentRecording.startTime)
              .toISOString()
              .replace("Z", "+0000");
            return restapi
              .getCameraRecording(
                that.camera.cameraId,
                that.currentRecording.recordingId,
                startTime,
                that.requestId
              )
              .then(function(chunks) {
                that.recordingChunks = chunks;
                that.selectedRecordingChunk = that.recordingChunks.find(c => {
                  return c.closed
                    ? new Date(c.startTime).getTime() <= that.currentTime &&
                        that.currentTime < new Date(c.endTime).getTime()
                    : that.currentTime >= new Date(c.startTime).getTime();
                });
              })
              .catch(error => {
                console.error(error);
              })
              .then(() => {
                return true;
              });
          }
        });
    },
    cleanUpEvents(min, max) {
      if (this.events.length > 1000) {
        this.events = this.events.filter(event => {
          const time = new Date(event.timestamp).getTime();
          return time < max && time > min;
        });
      }
    },
    cleanUpRecordings(min, max) {
      if (this.recordings.length > 1000) {
        this.recordings = this.recordings.filter(recording => {
          let startTime = new Date(recording.startTime).getTime();
          let endTime = recording.closed
            ? new Date(recording.endTime).getTime()
            : new Date().getTime();
          return (
            (startTime < min && endTime < max && endTime > min) ||
            (startTime > min && startTime < max && endTime > max) ||
            (endTime < max && startTime > min) ||
            (endTime > max && startTime < min)
          );
        });
      }
    },
    sortRecordings() {
      this.recordings.sort((a, b) => {
        return (
          new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
        );
      });
    },
    setCameraSnapshot(timestamp = null) {
      console.log(
        "setCameraSnapshot",
        timestamp
          ? new Date(this.currentTime).toLocaleTimeString()
          : new Date().toLocaleTimeString()
      );
      if (this.camera.status && this.camera.status.online) {
        const that = this;
        const ts = timestamp
          ? new Date(this.currentTime).toISOString().replace("Z", "+0000")
          : null;
        if (this.counter < 1) {
          this.counter++;
          return restapi
            .getSnapshot(this.camera.cameraId, this.resolution, ts)
            .then(function(snapshot) {
              //Not deleting as it might be useful in future
              // that.$set(that.camera, 'snapshot', snapshot)
              // that.previousSnapshot = snapshot
              if (that.player) {
                that.player.poster(snapshot);
              }
            })
            .catch(error => {
              console.error(error);
            })
            .then(() => {
              return true;
            });
        }
      }
    },
    async getCodecs() {
      let streams
      if (!this.footageShouldDestroy && this.camera.streams) {
        streams = this.camera.streams
      } else {
        streams = await restapi.getCameraStreams(this.camera.cameraId)
      }
      this.codec = streams[0].outputStreamCodecs.video;
      this.useRecordingChunks = this.codec !== "mjpeg";
    },
    async loadCameraData() {
      this.getCodecs()

      const event = await restapi.getFirstCameraEvent(this.camera.cameraId, this.requestId)
      console.log("First event", event);
      this.firstEvent = event;

      this.loadMoreData();

      if (this.footageShouldDestroy) return;
      const recording = await restapi.getFirstCameraRecording(this.camera.cameraId, this.requestId)
      console.log("First recording", recording);
      this.firstRecording = recording;

      let minDate = new Date();
      if (recording) {
        minDate = new Date(recording.startTime);
      } else {
        minDate = new Date();
        minDate.setDate(minDate.getDate() - this.camera.subscriptionRights.daysOfRecordings);
      }
      minDate.setHours(0);
      minDate.setMinutes(0);
      minDate.setSeconds(0);
      minDate.setDate(minDate.getDate() - this.camera.subscriptionRights.daysOfRecordings);
          
      const recordings = await restapi.getAllCameraRecordings(this.camera.cameraId, null, minDate.getTime(), this.requestId)
      this.mergeRecordings(recordings);
      this.recordingsRetrievalPending = false;

      let envData = Bowser.parse(window.navigator.userAgent);
      this.browser = envData.browser.name;
      if (
        this.browser == "Firefox" &&
        streams && 
        streams[0].outputStreamCodecs?.audio?.includes("g711") &&
        this.camera.detectionCapabilities.audio
      ) {
        store.dispatch("toastMessage", {
          showing: true,
          text:
            "This browser does not supports audio for this clip. To receive audio please use Google Chrome, Microsoft Edge or Apple Safari",
          timeout: -1,
          color: "error",
          support: true
        });
      }
      this.availableEventTypes = await restapi.getAvailableEventTypes(this.camera.cameraId)
    },
    async updateRecordings(deletedChunksExtremes) {
      try {
        console.log(deletedChunksExtremes);
        let recordings = await restapi.getCameraRecordings(
          this.camera.cameraId,
          null,
          null,
          100,
          this.requestId
        );
        this.recordings = recordings;

        if (this.useRecordingChunks && this.selectedRecording) {
          console.log("Now loading the chunks for the first recording");
          const startTime = new Date(this.selectedRecording.startTime)
            .toISOString()
            .replace("Z", "+0000");
          let chunks = await restapi.getCameraRecording(
            this.camera.cameraId,
            this.selectedRecording.recordingId,
            startTime,
            this.requestId
          );
          this.recordingChunks = chunks;
        }

        let startTime = deletedChunksExtremes.firstDeletedChunk.startTime;
        let endTime = deletedChunksExtremes.lastDeletedChunk.endTime;

        this.events = this.events.filter(event => {
          return event.timestamp < startTime || event.timestamp > endTime;
        });
      } catch (error) {
        console.error(error);
      }
    },
    async updateLatestRecordingChunks() { //updates the recording chunks of the ongoing cloud recording
      try {
        const latestRecording = this.recordings.reduce(function(a, b) {
          if (new Date(a.startTime).getTime() > new Date(b.startTime).getTime()) {
            return a
          } else return b
        })
        const pendingRecording = latestRecording.closed ? null : latestRecording
          
        if (!pendingRecording || pendingRecording?.recordingId !== this.selectedRecording?.recordingId) return
        
        const currentEndTime = this.recordingChunks[this.recordingChunks.length - 1].endTime

        if (this.useRecordingChunks && pendingRecording) {
          const startTime = new Date(pendingRecording.startTime)
            .toISOString()
            .replace("Z", "+0000");
          let chunks = await restapi.getCameraRecording(
            this.camera.cameraId,
            pendingRecording.recordingId,
            startTime,
            this.requestId
          );
          this.recordingChunks = chunks;
          if (this.recordingChunks[this.recordingChunks.length - 1].endTime !== currentEndTime) {
              this.$refs.timeline.updateFilteredRecordings()
            if (this.autoPlayChunkUpdate) {// if the chunk has been updated and the player ran out of footage, revive the player
              this.setPlayerCurrentTime(this.currentTime + 1)
              if (this.currentRecording)
                this.recordingSelected(
                  this.currentRecording.recordingId,
                  this.currentTime
                );
            }
          }
        }
      } catch (error) {
        console.error(error);
      }
    },
    updateOpenRecordings() { // This updates recordings there where being synced while the recording were retrieved.
      // Recordings that are being synced with edge storage are 'closed: false', but do have a provisional endTime.
      // But when the sync job is done, the endTime will change.
      const openRecordings = this.recordings.filter(recording => {
        return !recording.closed && recording.endTime
      })
      openRecordings.forEach(async (recording) => {
        try {
          const updatedRecording = await restapi.getCameraRecording(
            recording.cameraId,
          recording.recordingId,
          null,
          this.requestId
          )
          console.log("Updated an open recording", updatedRecording);
          let hash = this.hashRecordings;
          hash[recording.recordingId] = updatedRecording;
          this.recordings = Object.values(hash);
          this.sortRecordings();
        } catch(error) {
          console.log(error)
        }
      })
    },
    calculateEventFilter() {
      if (store.getters.footageEvents) {
        this.selectedEventFilters = store.getters.footageEvents;
        store.dispatch("setFootageEvents", null);
      } else {
        //To prevent flooding the timeline with events, we apply these filters by default
        if (this.supportsAnalyticsObject)
          this.selectedEventFilters.push("AnalyticsObject");
        if (this.supportsAnalyticsPerson)
          this.selectedEventFilters.push("AnalyticsPerson");
      }
    },
    setDewarpPlayerVolume(toggleMute) {
      if (toggleMute) {
        if (this.player.muted()) this.player.muted(false)
          else this.player.muted(true)
      } else {
        if (this.dewarpVolume) {
          if (this.player.muted()) this.player.muted(false)
          this.player.volume(this.dewarpVolume/100)
        } else if (this.dewarpVolume === 0) {
          this.player.muted(true)
        }
      }
    },
    setDewarpPlayBackRate(rate) {
      this.immervisionPlayer.video_html.playbackRate = rate
      this.player.playbackRate(rate)
    },
    onVideojsError() {
      if (store.getters.supportAccess) {
        store.dispatch("toastMessage", {
          text: this.$t("No video is available when using support-access"),
          color: "error",
          showing: true,
          timeout: -1,
          support: false
        });
      }
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../assets/styles/main";

.footage {
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  background-color: #eee;
  width: 100%;
  height: 100%;
  overflow: hidden;
  .layout {
    background-color: white;
    border-radius: $double-border-radius;
  }
  .camera-title {
    position: relative;
    top: 10px;
    left: 15px;
    z-index: 10;
    text-align: left;
    height: 0;
  }
  .footage-player {
    background-color: #000000; /* #959595; */
    overflow: hidden;
    position: relative;
    width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 8px !important;
    .camera-toolbar {
      position: absolute;
      width: 100%;
      left: 0;
      height: 0;
      z-index: 1;
      &.top {
        background-color: transparent;
        color: white;
        top: 0;
      }
      &.bottom {
        bottom: 0px;
        background-color: black;
        opacity: 0.7;
        border-radius: 0 0 $double-border-radius $double-border-radius;
        #play-svg-2 {
          fill: white;
          height: 20px;
          width: 20px;
        }
      }
      .camera-name {
        text-shadow: 0 0 3px #000000, 0 0 8px #000000;
      }
    }
    .immervisionContainer {
      height: 100% !important;
      width: 100% !important;
      overflow: hidden;
      &::v-deep canvas {
        display: block !important;
        max-height: 100% !important;
        max-width: 100% !important;
        margin: auto;
        display: block;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 0 !important;
      }
      .dewarp-arrow-controls {
        position: absolute;
        z-index: 1;
        left: 20px;
        top: 45px;
      }
    }
    &::v-deep .video-js {
      height: 100%;
      width: 100%;
      border-radius: 8px;
      max-width: 100%;
      .vjs-tech {
        height: 100%;
        outline: none;
        object-fit: contain;
        border-radius: 8px;
        background-color: #000000 !important;
      }
      .vjs-poster {
        border-radius: 8px;
        object-fit: contain;
        background-size: contain;
      }
      .vjs-control-bar {
        border-radius: 0 0 8px 8px;
        display: flex;

        .vjs-fullscreen-control {
          position: absolute;
          right: 0;
        }

        .icon-time-jump {
          background-image: url("../assets/images/forward-10.png");
          &.flip {
            background-image: url("../assets/images/replay-10.png");
          }
          background-size: 22px 22px;
          background-repeat: no-repeat;
          background-position: center;
        }

        .icon-snapshot {
          font-family: "Font Awesome 5 Free";
          font-weight: normal;
          font-style: normal;
          display: inline-block;
          text-decoration: inherit;
          font-weight: 900;
          font-size: 13px;
        }
        .icon-snapshot:before {
          content: "\f030";
        }

        .go-live {
          font-weight: 500 !important;
          text-transform: uppercase;
          display: inline-block;
          text-decoration: inherit;
          font-weight: 900;
          font-size: 13px;
          position: absolute;
          right: 0;
          margin-right: 40px;
          padding: 10px 10px 7px 10px;
          cursor: pointer;
        }
      }
      .vjs-menu li.vjs-selected {
        background-color: #b4b4b4;
      }
      &.offline {
        &::v-deep .video-js {
          background-color: $color-panel-grey;
          border-radius: 8px;
          .vjs-tech {
            border-radius: 8px;
            background-color: $color-panel-grey;
          }
        }
      }
    }
    .mjpeg-player {
      height: 100%;
      margin: auto;
      display: block;
      padding: auto;
      background-color: #000000;
    }
    .no-recording {
      position: absolute;
      top: calc(50% - 75px);
      left: calc(50% - 240px);
      height: 150px;
      z-index: 1;

      box-sizing: border-box;
      padding: 20px;

      .icon {
        margin-left: calc(50% - 30px);
      }
    }
    .play-pause {
      position: absolute;
      top: calc(50% - 50px);
      left: calc(50% - 50px);
      width: 100px;
      height: 100px;
      z-index: 1;

      box-sizing: border-box;
      padding: 20px;

      border: none;
      outline: none;

      fill: rgb(255, 255, 255);
      background-color: rgba(0, 0, 0, 0.5);

      border-radius: 50%;
      box-shadow: 0 5px 10px rgba(0, 0, 0, 0.25);
    }
  }
  svg {
    &.rotate {
      animation: progress-circular-rotate 1.4s linear infinite;
      transform-origin: center center;
      transition: all 0.2s ease-in-out;
    }
  }
  .timeline-action-bar {
    border-top: 1px solid #d3d3d39f;
    background-color: white;
    height: 41px;
    padding: 4px 10px;
  }
  .date-heading {
    letter-spacing: 1px;
    color: $color-general-text;
  }
  .vjs-poster {
    background-size: cover !important;
    border-radius: 8px;
  }
  &::v-deep .v-icon {
    &.rotate-left {
      transform: rotate(-90deg);
    }
    &.rotate-right {
      transform: rotate(90deg);
    }
  }
  &::v-deep .v-card {
    &.event-tooltip {
      width: 250px;
      height: 215px;
      position: absolute;
      visibility: hidden;
      border-radius: $double-border-radius;
    }
  }
  .event-image-hover {
    height: 125px;
    margin: auto;
    width: 100%;
  }
  .date-time-select {
    height: 32px;
    cursor: pointer;
    border-radius: $border-radius;
    background-color: $color-panel-grey;

    &.time {
      width: 140px;
    }
  }
  .action-button-group {
    background-color: #eee !important;
    button {
      height: 32px !important;
      background-color: #eee !important;
      border: none;
      &:nth-child(n + 2) {
        border-left: 1px solid black;
      }
    }
  }
}
.center-align {
  display: flex;
  align-content: center;
  justify-content: center;
}
.closeButton {
  position: absolute;
}
.small-top-margin {
  margin-top: 2px;
}
.menuContext {
  background: #000000cf;
}
.menuContextItems {
  color: white;
  width: 100%;
}
.menuContextItems:hover {
  width: 100%;
  background: #0000008f;
  color: white;
}

.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter-active {
  transition: opacity 0.001s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.button-group {
  height: 32px;
  background-color: #eeeeee;
  border-radius: 4px;
  margin-right: 4px;
  padding: 2px 4px;
  p {
    font-size: 14px;
  }
}
.go-live-dewarp {
  font-weight: 500 !important;
  text-transform: uppercase;
  color: white;
  display: inline-block;
  text-decoration: inherit;
  font-weight: 900;
  font-size: 13px;
  position: absolute;
  right: 0;
  margin-right: 40px;
  padding: 10px 10px 7px 10px;
  cursor: pointer;
}

.filter-text {
  font-size: 14px;
}

.time-picker {
  outline: none;
  height: 100%;
  padding-left: 6px;

  &::v-deep input {
    height: 100%;
    outline: none;
    cursor: pointer;
    user-select: none;
    border: 0 !important;
  }

  &::v-deep .dropdown {
    top: calc(-10.2em + 2px) !important;
    border-radius: 4px;
    .select-list {
      border-radius: 4px;
    }
  }
  &::v-deep .active {
    background-color: $primary !important;
  }

  &::v-deep .controls {
    padding-right: 6px;
  }
}
.width100 {
  width: 100%;
}
.time-picker {
  &::v-deep .time-picker-overlay {
    display: none;
  }
}
.footage-player-stamp {
  height: 30px;
  margin-top: 10px;
  color: #ffffff9e;
  text-shadow: 0 0 5px #000000, 0 0 6px #000000;
  text-align: right;
}
.filter-chips {
  width: auto;
}
.selected-playbackrate {
  background-color: #8080805d;
}
.dewarp-volume-slider { 
  width: 50px;
}
</style>
